import { Breadcrumb, Heading, Page } from 'components';
import { useCallback, useState, createContext, useContext, type ReactElement } from 'react';
import useSWR, { type KeyedMutator } from 'swr';
import { GenericTask, TaskResponse, TaskStatus, TaskWorkBucket, taskWorkBuckets } from './types';
import { Badge, Box, DataTable, LoadingContainer, Stack, TableColumns } from '@ff-it/ui';
import * as c from './columns';
import { TaskStatusSelector } from './TaskStatusSelector';
import { fetcher } from 'services';
import { actionErrorOrThrow } from 'utilities';
import { useIndicatorInvalidate } from 'core/Indicators';

const MutateContext = createContext<KeyedMutator<GenericTask[]> | null>(null);

function useMutateWithTracking() {
  const mutate = useContext(MutateContext);
  if (!mutate) {
    throw new Error('useMutateWithTracking must be used within MutateContext.Provider');
  }
  return mutate;
}

function WorkTaskStatusSelector({ task: { id, status, object } }: { task: GenericTask }): ReactElement {
  const mutateWithTracking = useMutateWithTracking();
  const invalidate = useIndicatorInvalidate();

  return (
    <TaskStatusSelector
      state={status}
      onStatusChange={async (status: TaskStatus) => {
        await mutateWithTracking(async (data) => {
          await fetcher<TaskResponse>({
            method: 'PATCH',
            url: `tasks/${object.app}:${object.model}:${object.id}/${id}/`,
            body: { status },
          }).catch(actionErrorOrThrow);
          return data;
        });
        await invalidate('late_tasks');
      }}
    />
  );
}

const columns: TableColumns<GenericTask> = [
  {
    key: 'status',
    width: '130px',
    render: (task) => <WorkTaskStatusSelector task={task} />,
  },
  c.name,
  c.context,
  c.author,
  c.created,
  c.dueDate,
  c.assignees,
  c.topic,
];

function groupTasksByWorkBucket(tasks: GenericTask[]): Record<TaskWorkBucket, GenericTask[]> {
  const grouped = taskWorkBuckets.reduce(
    (acc, status) => {
      acc[status] = [];
      return acc;
    },
    {} as Record<TaskWorkBucket, GenericTask[]>,
  );

  tasks.forEach((task) => {
    // Should not be part of this set
    if (task.work_bucket === null) {
      return;
    }
    grouped[task.work_bucket].push(task);
  });

  return grouped;
}

export function TasksContainer({
  tasks,
  mutateWithTracking,
}: {
  tasks: GenericTask[];
  mutateWithTracking: KeyedMutator<GenericTask[]>;
}): ReactElement {
  const groupedTasks = groupTasksByWorkBucket(tasks);

  return (
    <MutateContext.Provider value={mutateWithTracking}>
      <Stack direction="vertical">
        {taskWorkBuckets.map((bucket) => {
          const groupTasks = groupedTasks[bucket];
          return (
            <Stack direction="vertical" key={bucket} mb="3">
              <Box display="flex" alignItems="center" gap="1">
                <Box fontSize="xs" fontWeight="semibold" color="secondary" display="flex">
                  {bucket}
                </Box>
                <Badge count={groupTasks.length} showZero variant="secondary" />
              </Box>
              <DataTable
                fixed
                size="sm"
                columns={columns}
                data={groupTasks}
                emptyElement={<Box color="disabled">No tasks here</Box>}
              />
            </Stack>
          );
        })}
      </Stack>
    </MutateContext.Provider>
  );
}

export function MyWork(): ReactElement {
  const {
    data: tasks,
    mutate,
    isLoading,
    error,
  } = useSWR<GenericTask[]>({
    url: 'tasks/dashboard/my_work/',
    method: 'GET',
    // @TODO: should be sane limit, but we don't want pagination
    queryParams: { page_size: null },
  });

  const [isMutating, setIsMutating] = useState(false);
  const mutateWithTracking = useCallback(
    (data: any, opts?: any) => {
      const promise = mutate(data, opts);

      setIsMutating(true);
      promise.finally(() => setIsMutating(false));

      return promise;
    },
    [mutate],
  );

  if (error) {
    throw error;
  }

  return (
    <Page>
      <Breadcrumb to={null}>My work</Breadcrumb>
      <Heading title="My work" />
      <LoadingContainer loading={isLoading || isMutating}>
        {tasks && <TasksContainer tasks={tasks} mutateWithTracking={mutateWithTracking} />}
      </LoadingContainer>
    </Page>
  );
}
